<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { onBeforeUnmount, onMounted } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  contactName: { type: String, required: true },
  hasAcelerity: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  error: { type: String, default: '' },
});
const emit = defineEmits(['close', 'confirm']);
const close = () => {
  if (!props.isSaving) emit('close');
};
const onKeydown = event => {
  if (event.key === 'Escape') close();
};
onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <div
      class="team-remove-backdrop fixed inset-0 z-[2147483550] grid place-items-center overflow-y-auto bg-[rgba(20,24,31,0.35)] p-4"
      @click.self="close"
    >
      <section
        class="team-remove-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="`Remover ${contactName} da equipe?`"
      >
        <button
          type="button"
          class="team-remove-modal__close"
          aria-label="Fechar"
          :disabled="isSaving"
          @click="close"
        >
          <Icon icon="i-lucide-x" class="size-4" />
        </button>
        <span class="team-remove-modal__icon" aria-hidden="true">
          <Icon icon="i-lucide-circle-minus" class="size-5" />
        </span>
        <div class="team-remove-modal__intro">
          <h2>Remover {{ contactName }} da equipe?</h2>
          <p>
            Ele sai desta lista e volta a ser tratado como um cliente comum.
          </p>
        </div>
        <div class="team-remove-modal__effects">
          <div class="team-remove-modal__effect">
            <b
              class="team-remove-modal__marker team-remove-modal__marker--danger"
              >•</b
            >
            <span
              >A IA, o funil e as automações passam a responder e processar as
              mensagens dele.</span
            >
          </div>
          <div v-if="hasAcelerity" class="team-remove-modal__effect">
            <b
              class="team-remove-modal__marker team-remove-modal__marker--danger"
              >•</b
            >
            <span
              >Fica registrado como decisão manual: a sincronização não vai
              marcar de novo.</span
            >
          </div>
          <div class="team-remove-modal__effect">
            <b
              class="team-remove-modal__marker team-remove-modal__marker--success"
              >↺</b
            >
            <span>Dá para desfazer depois em “Removidos”.</span>
          </div>
        </div>
        <p v-if="error" class="team-remove-modal__error" role="alert">
          {{ error }}
        </p>
        <div class="team-remove-modal__actions">
          <button type="button" :disabled="isSaving" @click="close">
            Cancelar
          </button>
          <button
            type="button"
            class="team-remove-modal__confirm"
            :disabled="isSaving"
            @click="emit('confirm')"
          >
            {{ isSaving ? 'Removendo…' : 'Remover da equipe' }}
          </button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.team-remove-modal {
  position: relative;
  width: min(480px, 100%);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
  color: #1f2328;
  font-family: Inter, sans-serif;
}
.team-remove-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: #8c959f;
}
.team-remove-modal__close:hover {
  background: #f5f5f3;
  color: #1f2328;
}
.team-remove-modal__icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: #fef3f2;
  color: #b42318;
}
.team-remove-modal__intro {
  display: grid;
  gap: 6px;
}
.team-remove-modal__intro h2 {
  margin: 0;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
  color: #1f2328;
}
.team-remove-modal__intro p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #57606a;
}
.team-remove-modal__effects {
  display: grid;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7f8f7;
}
.team-remove-modal__effect {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  font-size: 13px;
  line-height: 1.45;
  color: #444c56;
}
.team-remove-modal__marker {
  line-height: 1.45;
  font-size: 13px;
  font-weight: 700;
}
.team-remove-modal__marker--danger {
  color: #b42318;
}
.team-remove-modal__marker--success {
  color: #15803d;
}
.team-remove-modal__error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3f2;
  color: #b42318;
  font-size: 13px;
}
.team-remove-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.team-remove-modal__actions button {
  height: 40px;
  padding: 0 16px;
  border: 1px solid #d0d4da;
  border-radius: 10px;
  background: #fff;
  color: #1f2328;
  font-size: 14px;
}
.team-remove-modal__actions .team-remove-modal__confirm {
  border: 0;
  background: #b42318;
  color: #fff;
  font-weight: 600;
}
.team-remove-modal__actions button:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
